import React, { useEffect, useState } from 'react';
import HelpHeader from './HelpHeader';
import HelpSection from './HelpSection';
import HelpCard from './HelpCard';
import FaqAccordion from './FaqAccordion';
import HelpFooter from './HelpFooter';

const HelpPage: React.FC = () => {
  useEffect(() => {
    document.title = 'ヘルプ | RepliGo（レプリゴ）';
  }, []);

  const faqs = [
    {
      question: 'Q. Googleアカウント連携がうまくいかない場合は？',
      answer: 'A. Googleアカウントで「Google Business Profile」の権限が付与されていることを確認してください。店舗のオーナーまたは管理者権限が必要です。'
    },
    {
      question: 'Q. 店舗が表示されない場合は？',
      answer: 'A. 連携したGoogleアカウントに紐づくGoogle Business Profileの店舗のみ表示されます。Googleビジネスプロフィールで店舗が登録されているかご確認ください。'
    },
    {
      question: 'Q. 口コミが同期されない場合は？',
      answer: 'A. Google Business Profile APIとの通信状況によります。しばらく待って「口コミ同期」ボタンを再度押してください。解決しない場合は、ご意見・ご要望からご連絡ください。'
    },
    {
      question: 'Q. Google連携を解除したい場合は？',
      answer: 'A. 画面右上のユーザーアイコンをクリックし、メニューから「Google連携を解除」を選択してください。'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <HelpHeader />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <HelpSection id="overview" title="システム概要">
          <HelpCard>
            <p className="leading-relaxed">
              RepliGo（レプリゴ）は、Google Business Profile APIと連携して口コミを管理・返信するSaaS型の口コミ管理ツールです。<br />
              Googleアカウントを連携するだけで、あなたの店舗の口コミを簡単に収集・分析・返信できます。
            </p>
          </HelpCard>
        </HelpSection>

        <HelpSection id="features" title="主な機能">
          <HelpCard>
            <ul className="space-y-4">
              <li>
                <h3 className="font-medium text-lg mb-1">Googleアカウント連携</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>Googleアカウントを連携するだけで口コミ管理を開始</li>
                  <li>Google Business Profileに登録された店舗を自動取得</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">口コミの同期</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>「口コミ同期」ボタンで最新の口コミを取得</li>
                  <li>Google Business Profile APIから直接データを取得</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">口コミ一覧・フィルタ</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>取得した口コミを一覧表示</li>
                  <li>並べ替え（新しい順・評価順）や返信有無で絞り込み</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">AIによる返信文自動生成</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>過去の返信例、定型文、キーワードをもとに自動生成</li>
                  <li>「返信自由生成」でテスト可能</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">定型文・キーワード設定</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>よく使う表現や強調語句を登録して自動返信に活用</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">アンケート・QRコード</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>顧客向けアンケートフォームを作成</li>
                  <li>QRコードで簡単にアンケートを配布</li>
                  <li>回答からAIが口コミ下書きを自動生成</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg mb-1">分析ダッシュボード</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>口コミデータの可視化と分析</li>
                  <li>KPI指標の表示（総口コミ数、平均評価、返信率など）</li>
                  <li>時系列チャートによる評価推移の確認</li>
                  <li>詳細分析（評価分布、キーワード分析など）</li>
                  <li>改善提案の自動生成</li>
                </ul>
              </li>
            </ul>
          </HelpCard>
        </HelpSection>

        <HelpSection id="guide" title="使い方ガイド">
          <HelpCard>
            <ol className="space-y-4 list-decimal pl-5">
              <li>
                <h3 className="font-medium text-lg">ログイン</h3>
                <p className="text-gray-700">登録メールアドレスでログイン</p>
              </li>
              <li>
                <h3 className="font-medium text-lg">Googleアカウント連携</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>「Googleアカウント連携」ボタンをクリック</li>
                  <li>Googleアカウントでログインし、権限を許可</li>
                  <li>連携が完了すると、店舗が自動的に表示されます</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg">口コミの同期</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>「口コミ同期」ボタンをクリックして最新の口コミを取得</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg">口コミの確認・返信</h3>
                <p className="text-gray-700">一覧から確認・返信生成・カスタム可能</p>
              </li>
              <li>
                <h3 className="font-medium text-lg">設定</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>定型文・キーワード設定</li>
                  <li>アンケート・QRコード設定</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg">分析ダッシュボード</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  <li>「分析」ボタンからアクセス</li>
                  <li>期間や店舗を選択して分析データを表示</li>
                  <li>KPI指標や時系列チャートで口コミの傾向を確認</li>
                </ul>
              </li>
              <li>
                <h3 className="font-medium text-lg">フィードバック</h3>
                <p className="text-gray-700">ユーザーメニュー → ご意見・ご要望 から送信</p>
              </li>
            </ol>
          </HelpCard>
        </HelpSection>

        <HelpSection id="faq" title="よくある質問（FAQ）">
          <HelpCard>
            <FaqAccordion faqs={faqs} />
          </HelpCard>
        </HelpSection>

        <HelpSection id="support" title="サポート">
          <HelpCard>
            <p className="text-gray-700">
              ご不明点・不具合・ご要望は「ご意見・ご要望」からご連絡ください。
            </p>
          </HelpCard>
        </HelpSection>
      </main>

      <HelpFooter />
    </div>
  );
};

export default HelpPage;
